/**
 * useAdminAuth Hook
 * 관리자 인증 관련 커스텀 훅
 */

import { useState, useCallback } from 'react';
import { AdminAuthRepository } from '../repositories/AdminAuthRepository';
import { AdminAuthService } from '../services/AdminAuthService';
import {
  AdminUser,
  AdminLoginResponse,
  UpdateProfileRequest,
  CreateOperatorRequest,
} from '../models';

const authRepository = new AdminAuthRepository();
const authService = new AdminAuthService(authRepository);

export const useAdminAuth = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(
    authService.getCurrentUser()
  );

  /**
   * 로그인
   */
  const login = useCallback(
    async (email: string, password: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const response: AdminLoginResponse = await authService.login(
          email,
          password
        );
        // 백엔드가 'user' 필드로 반환하므로 처리
        const user = (response as any).user || response.admin_user;
        setCurrentUser(user);
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '로그인에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * 로그아웃
   */
  const logout = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      await authService.logout();
      setCurrentUser(null);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '로그아웃에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 프로필 조회 (새로고침)
   */
  const refreshProfile = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);

    try {
      const profile = await authService.getProfile();
      setCurrentUser(profile);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : '프로필 조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 프로필 수정
   */
  const updateProfile = useCallback(
    async (request: UpdateProfileRequest): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        const updatedProfile = await authService.updateProfile(request);
        setCurrentUser(updatedProfile);
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : '프로필 수정에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * 비밀번호 변경
   */
  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        await authService.changePassword(currentPassword, newPassword);
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : '비밀번호 변경에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * Operator 생성 (super_admin만 가능)
   */
  const createOperator = useCallback(
    async (request: CreateOperatorRequest): Promise<AdminUser | null> => {
      setLoading(true);
      setError(null);

      try {
        const newOperator = await authService.createOperator(request);
        return newOperator;
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : 'Operator 생성에 실패했습니다.';
        setError(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * 로그인 상태 확인
   */
  const isAuthenticated = useCallback((): boolean => {
    return authService.isAuthenticated();
  }, []);

  return {
    currentUser,
    loading,
    error,
    login,
    logout,
    refreshProfile,
    updateProfile,
    changePassword,
    createOperator,
    isAuthenticated,
  };
};
